import React, { useState, useEffect } from 'react';
import { db } from '../firebase/firestore';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import moment from 'moment';
import Spinner from './Spinner';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const StatisticsPanel = () => {
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeUsersLast24Hours, setActiveUsersLast24Hours] = useState(0);
  const [activeUsersLast1Minute, setActiveUsersLast1Minute] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [uniqueOrderUsers, setUniqueOrderUsers] = useState(0);
  const [ordersPerDay, setOrdersPerDay] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [bannerImage, setBannerImage] = useState('');
  const [bannerLink, setBannerLink] = useState('');

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    const last24Hours = moment().subtract(24, 'hours').toDate();
    const last1Minute = moment().subtract(1, 'minute').toDate();

    try {
      // Users
      const usersSnapshot = await getDocs(collection(db, "telegramUsers"));
      const usersData = usersSnapshot.docs.map(doc => doc.data());
      setTotalUsers(usersData.length);
      setActiveUsersLast24Hours(usersData.filter(u => u.lastActive?.toDate() > last24Hours).length);
      setActiveUsersLast1Minute(usersData.filter(u => u.lastActive?.toDate() > last1Minute).length);

      // Orders
      const ordersSnapshot = await getDocs(collection(db, "orders"));
      const ordersData = ordersSnapshot.docs.map(doc => doc.data());
      setTotalOrders(ordersData.length);
      setTotalRevenue(ordersData.reduce((acc, o) => acc + (parseFloat(o.total) || 0), 0));
      setUniqueOrderUsers(new Set(ordersData.map(o => o.userId)).size);

      // Orders per day
      const grouped = {};
      ordersData.forEach(order => {
        if (order.createdAt?.toDate) {
          const dateStr = moment(order.createdAt.toDate()).format('YYYY-MM-DD');
          grouped[dateStr] = (grouped[dateStr] || 0) + 1;
        }
      });
      setOrdersPerDay(Object.entries(grouped)
        .sort(([a], [b]) => new Date(a) - new Date(b))
        .map(([date, count]) => ({ date, count }))
      );

      // Banners
      const bannersSnapshot = await getDocs(collection(db, "banners"));
      setBanners(bannersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

    } catch (err) {
      console.error('Ошибка при получении статистики:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (typeof num !== "number") return "Invalid";
    if (num < 1 && num.toString().split('.')[1]?.length > 3) return num.toFixed(6).replace(/0+$/, '');
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const statista = [
    { title: 'Total Users', count: totalUsers },
    { title: 'Active 24h', count: activeUsersLast24Hours },
    { title: 'Online Now', count: activeUsersLast1Minute },
    { title: 'Orders', count: totalOrders },
    { title: 'Revenue €', count: formatNumber(totalRevenue) },
    { title: 'Buyers', count: uniqueOrderUsers },
  ];

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await fetch("/api/upload", { method: "POST", body: formData });
      const data = await res.json();

      if (data.url) {
        const fullUrl = `${window.location.origin}${data.url}`;
        setBannerImage(fullUrl);

        await addDoc(collection(db, "banners"), {
          imageUrl: fullUrl,
          link: bannerLink || "",
          createdAt: new Date(),
        });

        fetchStatistics();

        setBannerImage('');
        setBannerLink('');
      }
    } catch (err) {
      console.error("Upload error:", err);
      alert("Failed to upload image");
    }
  };

  return loading ? <Spinner /> : (
    <div className="w-full flex flex-col space-y-6 h-[100vh] scroller pt-4 overflow-y-auto pb-[150px]">

      {/* Stats */}
      <div className="w-full flex flex-wrap gap-4">
        {statista.map((s, i) => (
          <div key={i} className="bg-cards p-4 rounded-[10px] w-[47%] sm:w-[32%] h-[120px] flex flex-col justify-center space-y-3">
            <h2 className="text-[16px] sm:text-[18px] font-semibold text-[#bdbdbd]">{s.title}</h2>
            <span className="text-[20px] sm:text-[24px] text-[#fff] font-bold">{s.count}</span>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="w-full bg-cards rounded-[10px] p-6">
        <h2 className="text-xl font-bold text-white mb-4">График заказов по дням</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={ordersPerDay}>
            <CartesianGrid strokeDasharray="3 3" stroke="#888" />
            <XAxis dataKey="date" stroke="#ccc" />
            <YAxis stroke="#ccc" />
            <Tooltip />
            <Line type="monotone" dataKey="count" stroke="#00bfff" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Banners */}
      <div className="w-full bg-cards rounded-[10px] p-6">
        <h2 className="text-xl font-bold text-white mb-4">Баннеры</h2>

        {/* Upload */}
        <div className="space-y-2">
          <input
            type="url"
            name="bannerImage"
            placeholder="Image URL"
            value={bannerImage}
            onChange={(e) => setBannerImage(e.target.value)}
            className="w-full p-2 rounded bg-gray-700 text-white"
          />
          <input
            type="url"
            name="bannerLink"
            placeholder="Product or Category URL"
            value={bannerLink}
            onChange={(e) => setBannerLink(e.target.value)}
            className="w-full p-2 rounded bg-gray-700 text-white"
          />
          <input
            type="file"
            accept="image/*"
            onChange={handleBannerUpload}
            className="w-full p-2 rounded bg-gray-700 text-white"
          />
          {bannerImage && (
            <img
              src={bannerImage}
              alt="Preview"
              className="w-32 h-32 object-cover rounded"
            />
          )}
        </div>

        {/* Banners grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
          {banners.length
            ? banners.map((b) => (
                <div
                  key={b.id}
                  className="relative w-[360px] h-[120px] overflow-hidden rounded-lg border border-gray-700 cursor-pointer"
                  onClick={() => b.link && window.open(b.link, "_blank")}
                >
                  <img
                    src={b.imageUrl}
                    alt="banner"
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={async (e) => {
                      e.stopPropagation(); // чтобы клик не открывал ссылку
                      if (!window.confirm("Удалить этот баннер?")) return;

                      try {
                        await import('firebase/firestore').then(({ doc, deleteDoc }) =>
                          deleteDoc(doc(db, "banners", b.id))
                        );
                        setBanners(prev => prev.filter(x => x.id !== b.id));
                      } catch (err) {
                        console.error("Ошибка при удалении баннера:", err);
                        alert("Не удалось удалить баннер");
                      }
                    }}
                    className="absolute top-1 right-1 bg-red-600 text-white px-2 py-1 text-xs rounded hover:bg-red-700"
                  >
                    Удалить
                  </button>
                </div>
              ))
            : <p className="text-gray-400">There are no banners</p>}
        </div>

      </div>
    </div>
  );
};

export default StatisticsPanel;
